import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Search, ShieldCheck } from 'lucide-react';
import { chamarApi } from '@/nucleo/auth/cliente-api';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';

type Resultado =
  | { confirmado: true; situacao: string; proximoPasso: string; prazo: string | null }
  | { confirmado: false; situacao: 'NAO_CONFIRMADO'; mensagem: string };
type Estado = {
  fase: string;
  titulo: string;
  descricao: string;
  consulta: { protocolo: string; fator: ''; rotuloProtocolo: string; rotuloFator: string };
  permissoes: { anonima: boolean; consultar: boolean };
  resultado: Resultado | null;
  aviso: string | null;
  privacidade: string;
  comandos: Array<{ verbo: string; habilitado: boolean }>;
  navegacao: Array<{ rotulo: string; caminho: string }>;
};
type Envelope = { contract_version: string; screen: string; event: unknown; state: Estado };
const RAIZ = '/bff/v1/j02-s04-acompanhar-protocolo';

export function AcompanharProtocoloPage() {
  const [estado, setEstado] = useState<Estado | null>(null);
  const [protocolo, setProtocolo] = useState('');
  const [fator, setFator] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    void chamarApi<Envelope>(`${RAIZ}/state`, { publica: true })
      .then((r) => setEstado(r.state))
      .catch(() => setErro('O acompanhamento está temporariamente indisponível.'));
  }, []);
  async function executar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    const corpo = { protocolo, fator };
    try {
      const preparada = await chamarApi<Envelope>(`${RAIZ}/verbs/preparar`, {
        metodo: 'POST',
        publica: true,
        corpo,
      });
      setEstado(preparada.state);
      if (preparada.state.fase === 'INVALIDO') return;
      const validada = await chamarApi<Envelope>(`${RAIZ}/verbs/validar`, {
        metodo: 'POST',
        publica: true,
        corpo,
      });
      setEstado(validada.state);
      if (validada.state.fase !== 'VALIDADO') return;
      const concluida = await chamarApi<Envelope>(`${RAIZ}/verbs/confirmar`, {
        metodo: 'POST',
        publica: true,
        corpo,
      });
      setEstado(concluida.state);
    } catch {
      setErro('O acompanhamento está temporariamente indisponível. Tente novamente.');
    }
  }
  if (!estado && !erro)
    return (
      <div role="status" className="py-16 text-center text-muted-foreground">
        Carregando acompanhamento…
      </div>
    );
  return (
    <section
      className="mx-auto max-w-3xl space-y-6 px-4 py-10 sm:px-6"
      aria-labelledby="acompanhar-titulo"
    >
      <header className="space-y-2">
        <h1 id="acompanhar-titulo" className="text-3xl font-bold">
          {estado?.titulo ?? 'Acompanhar protocolo'}
        </h1>
        <p className="text-muted-foreground">{estado?.descricao}</p>
      </header>
      {(erro || estado?.aviso) && (
        <Alert variant="destructive">
          <AlertTitle>Não foi possível concluir</AlertTitle>
          <AlertDescription>{erro ?? estado?.aviso}</AlertDescription>
        </Alert>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Search className="h-5 w-5" aria-hidden="true" />
            Consultar andamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={executar}>
            <div className="space-y-2">
              <Label htmlFor="protocolo">Protocolo</Label>
              <Input
                id="protocolo"
                value={protocolo}
                onChange={(e) => setProtocolo(e.target.value.toUpperCase())}
                autoComplete="off"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fator">CPF ou CNPJ do titular</Label>
              <Input
                id="fator"
                value={fator}
                onChange={(e) => setFator(e.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                autoComplete="off"
                maxLength={14}
                required
              />
            </div>
            <Button type="submit" disabled={!estado?.permissoes.consultar} className="gap-2">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Consultar protocolo
            </Button>
          </form>
        </CardContent>
      </Card>
      {estado?.resultado && (
        <Card aria-live="polite">
          <CardHeader>
            <CardTitle className="text-lg">Andamento público</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {estado.resultado.confirmado ? (
              <>
                <p>
                  <strong>Situação:</strong> {estado.resultado.situacao}
                </p>
                <p>
                  <strong>Próximo passo:</strong> {estado.resultado.proximoPasso}
                </p>
                {estado.resultado.prazo && (
                  <p>
                    <strong>Prazo:</strong> {estado.resultado.prazo}
                  </p>
                )}
              </>
            ) : (
              <p>{estado.resultado.mensagem}</p>
            )}
          </CardContent>
        </Card>
      )}
      {estado && <p className="text-sm text-muted-foreground">{estado.privacidade}</p>}
    </section>
  );
}
